/**
 * Global configuration that replaces the window variables previously injected by Elixir
 * This allows us to run the application independently of the Phoenix server
 */

interface GlobalConfig {
  baseUrl: string;
  summarySsoUrl: string;
  insightsUrl: string;
  employersUrl: string;
  educationConnectionsUrl: string;
  ecpUrl: string;
  ispServiceUrl: string;
  gusUiBaseUrl: string;
  gusServiceUrl: string;
  brightpathHomeUrl: string;
  learnosityUrl: string;
  discoveryNextUrl: string;
  certificationServiceUrl: string;
  collegeServiceUrl: string;
  updatedBioEnabled: boolean;
  appEnv: string;
  fromUrl: string;
}

const getEnvironment = (): string => {
  // Prioritize environment variables over hostname detection
  const envVar = import.meta.env.VITE_APP_ENV || import.meta.env.VITE_NODE_ENV;
  if (envVar) {
    return envVar === 'development' ? 'dev' : envVar;
  }

  // Fallback to hostname detection
  const hostname = window.location.hostname;
  if (hostname.includes('local') || hostname === 'localhost') return 'dev';
  if (hostname.includes('dev')) return 'dev';
  if (hostname.includes('stage')) return 'stage';
  if (hostname.includes('sandbox')) return 'sandbox';
  return 'prod';
};

const createEnvironmentConfig = (env: string): GlobalConfig => {
  console.log('createEnvironmentConfig', env);
  const baseConfigs = {
    dev: {
      baseUrl: 'https://api.dev.youscience.com',
      summarySsoUrl: 'https://signin.dev.youscience.com/sso/new',
      insightsUrl: 'https://insights.dev.youscience.com',
      employersUrl: 'https://employers.dev.youscience.com/transition',
      educationConnectionsUrl: 'https://collegesandprograms.dev.youscience.com',
      ecpUrl: 'https://ecp.dev.youscience.com',
      ispServiceUrl: 'https://signin.dev.youscience.com',
      gusUiBaseUrl: 'https://admin.dev.youscience.com',
      gusServiceUrl: 'https://apigw.dev.youscience.com/gus',
      brightpathHomeUrl: 'https://brightpath.dev.youscience.com',
      learnosityUrl: 'https://discovery.dev.youscience.com',
      discoveryNextUrl: 'https://apigw.dev.youscience.com/discovery-next',
      certificationServiceUrl: 'https://apigw.dev.youscience.com/certs',
      collegeServiceUrl: 'https://apigw.dev.youscience.com/college',
    },
    sandbox: {
      baseUrl: 'https://api.sandbox.youscience.com',
      summarySsoUrl: 'https://summary.sandbox.youscience.com/sso/new',
      insightsUrl: 'https://insights.sandbox.youscience.com',
      employersUrl: 'https://employers.sandbox.youscience.com/transition',
      educationConnectionsUrl: 'https://collegesandprograms.sandbox.youscience.com',
      ecpUrl: 'https://ecp.sandbox.youscience.com',
      ispServiceUrl: 'https://signin.sandbox.youscience.com',
      gusUiBaseUrl: 'https://admin.sandbox.youscience.com',
      gusServiceUrl: 'https://apigw.sandbox.youscience.com/gus',
      brightpathHomeUrl: 'https://brightpath.sandbox.youscience.com',
      learnosityUrl: 'https://discovery.sandbox.youscience.com',
      discoveryNextUrl: 'https://apigw.sandbox.youscience.com/discovery-next',
      certificationServiceUrl: 'https://apigw.sandbox.youscience.com/certs',
      collegeServiceUrl: 'https://apigw.sandbox.youscience.com/college',
    },
    stage: {
      baseUrl: 'https://api.stage.youscience.com',
      summarySsoUrl: 'https://summary.stage.youscience.com/sso/new',
      insightsUrl: 'https://insights.stage.youscience.com',
      employersUrl: 'https://employers.stage.youscience.com/transition',
      educationConnectionsUrl: 'https://collegesandprograms.stage.youscience.com',
      ecpUrl: 'https://ecp.stage.youscience.com',
      ispServiceUrl: 'https://signin.stage.youscience.com',
      gusUiBaseUrl: 'https://admin.stage.youscience.com',
      gusServiceUrl: 'https://apigw.stage.youscience.com/gus',
      brightpathHomeUrl: 'https://brightpath.stage.youscience.com',
      learnosityUrl: 'https://discovery.stage.youscience.com',
      discoveryNextUrl: 'https://apigw.stage.youscience.com/discovery-next',
      certificationServiceUrl: 'https://apigw.stage.youscience.com/certs',
      collegeServiceUrl: 'https://apigw.stage.youscience.com/college',
    },
    prod: {
      baseUrl: 'https://api.youscience.com',
      summarySsoUrl: 'https://summary.youscience.com/sso/new',
      insightsUrl: 'https://insights.youscience.com',
      employersUrl: 'https://employers.youscience.com/transition',
      educationConnectionsUrl: 'https://collegesandprograms.youscience.com',
      ecpUrl: 'https://ecp.youscience.com',
      ispServiceUrl: 'https://signin.youscience.com',
      gusUiBaseUrl: 'https://admin.youscience.com',
      gusServiceUrl: 'https://apigw.youscience.com/gus',
      brightpathHomeUrl: 'https://brightpath.youscience.com',
      learnosityUrl: 'https://discovery.youscience.com',
      discoveryNextUrl: 'https://apigw.youscience.com/discovery-next',
      certificationServiceUrl: 'https://apigw.youscience.com/certs',
      collegeServiceUrl: 'https://apigw.youscience.com/college',
    },
  };

  const config = baseConfigs[env as keyof typeof baseConfigs] || baseConfigs.prod;

  return {
    ...config,
    updatedBioEnabled: env !== 'prod', // Feature flag - enable in non-prod environments
    appEnv: env,
    fromUrl: document.referrer,
  };
};

export const configureGlobalConfig = (): void => {
  const env = getEnvironment();
  const config = createEnvironmentConfig(env);
  console.log('config', config);
  console.log('env', env);

  // Set global window variables for backward compatibility with existing code
  (window as any).baseUrl = config.baseUrl;
  (window as any).summarySsoUrl = config.summarySsoUrl;
  (window as any).insightsUrl = config.insightsUrl;
  (window as any).employersUrl = config.employersUrl;
  (window as any).educationConnectionsUrl = config.educationConnectionsUrl;
  (window as any).ecpUrl = config.ecpUrl;
  (window as any).ispServiceUrl = config.ispServiceUrl;
  (window as any).gusUiBaseUrl = config.gusUiBaseUrl;
  (window as any).gusServiceUrl = config.gusServiceUrl;
  (window as any).brightpathHomeUrl = config.brightpathHomeUrl;
  (window as any).learnosityUrl = config.learnosityUrl;
  (window as any).discoveryNextUrl = config.discoveryNextUrl;
  (window as any).certificationServiceUrl = config.certificationServiceUrl;
  (window as any).collegeServiceUrl = config.collegeServiceUrl;
  (window as any).updatedBioEnabled = config.updatedBioEnabled;
  (window as any).appEnv = config.appEnv;
  (window as any).fromUrl = config.fromUrl;

  console.log(`🚀 Arya configured for environment: ${env}`);
};

export const getGlobalConfig = (): GlobalConfig => {
  const env = getEnvironment();
  console.log('getGlobalConfig', env);
  return createEnvironmentConfig(env);
};
